import { resolveSeatLicenseType } from '@/components/refRepsPortal/portalOrganization.js'
import { seatLicenseTypeFromUserCourse } from '@/utils/courseLicenseAccess.js'

function normalizeId(value) {
  if (value == null || value === '') return null
  return String(value)
}

/** Collect every catalog course id we can infer from a user-course row. */
function enrollmentCourseIds(enrollment) {
  if (!enrollment || typeof enrollment !== 'object') return []
  return [
    enrollment.id,
    enrollment.course_id,
    enrollment.courseId,
  ]
    .map(normalizeId)
    .filter(Boolean)
}

/** @param {Record<string, unknown> | null | undefined} activity */
export function getActivitySportCourseIds(activity) {
  if (Array.isArray(activity?.sportCourseIds) && activity.sportCourseIds.length > 0) {
    return activity.sportCourseIds.map(normalizeId).filter(Boolean)
  }
  return []
}

/**
 * Resolve sport course ids from activity.sportCourses names when ids are absent.
 * @param {Record<string, unknown> | null | undefined} activity
 * @param {{ id?: string | number, name?: string, type?: string }[]} catalogCourses
 */
export function resolveActivitySportCourseIds(activity, catalogCourses = []) {
  const direct = getActivitySportCourseIds(activity)
  if (direct.length) return direct

  const names = Array.isArray(activity?.sportCourses) ? [...activity.sportCourses] : []

  if (!names.length || !catalogCourses.length) return []

  const nameSet = new Set(
    names.filter(Boolean).map((n) => String(n).trim().toLowerCase())
  )
  return catalogCourses
    .filter(
      (c) =>
        c?.type === 'course'
        && nameSet.has(String(c.name ?? '').trim().toLowerCase())
    )
    .map((c) => normalizeId(c.id))
    .filter(Boolean)
}

/** @param {import('pinia').Store | Record<string, unknown>} store */
export function buildLearningActivityEligibilityOptions(store) {
  const catalogCourses =
    (store?.courses?.length ? store.courses : null)
    ?? store?.allCourses
    ?? []
  return { catalogCourses }
}

/** Learning activities use a simplified Basic | Premium tier (tierStatus). */
export function normalizeActivityTierStatus(activity) {
  const raw = activity?.tierStatus ?? activity?.tier_status ?? 'Basic'
  return String(raw).trim().toLowerCase() === 'premium' ? 'Premium' : 'Basic'
}

/** @param {Record<string, unknown> | null | undefined} activity */
export function isPremiumLearningActivity(activity) {
  return normalizeActivityTierStatus(activity) === 'Premium'
}

/** Map enrollment seat license to Basic | Premium for learning-activity gating. */
export function enrollmentTierForLearningActivity(userCourse) {
  const licenseType = resolveSeatLicenseType(seatLicenseTypeFromUserCourse(userCourse))
  if (licenseType === 'Premium' || licenseType === 'Supp. Premium') {
    return 'Premium'
  }
  return 'Basic'
}

/** @param {Record<string, unknown> | null | undefined} student */
export function studentEnrollmentForCourseId(student, courseId) {
  const target = normalizeId(courseId)
  if (!target) return null

  for (const uc of student?.allUserCourses || []) {
    if (enrollmentCourseIds(uc).includes(target)) return uc
  }
  for (const uc of student?.interactiveCourses || []) {
    if (enrollmentCourseIds(uc).includes(target)) return uc
  }
  return null
}

/** Whether the learner has this catalog course (any source — org, purchase, etc.). */
export function studentHasCourseAssignment(student, courseId) {
  const target = normalizeId(courseId)
  if (!target) return false

  if ((student?.courseIds || []).map(normalizeId).includes(target)) {
    return true
  }
  if ((student?.interactiveIDs || []).map(normalizeId).includes(target)) {
    return true
  }

  return !!studentEnrollmentForCourseId(student, courseId)
}

/**
 * Whether a learner can be assigned this learning activity.
 * Basic activity: has a matching sportCourseIds course (any seat license).
 * Premium activity: has a matching course on a Premium seat license.
 *
 * @param {Record<string, unknown> | null | undefined} student
 * @param {Record<string, unknown> | null | undefined} activity
 * @param {{ catalogCourses?: unknown[] }} [options]
 */
export function studentEligibleForLearningActivity(student, activity, options = {}) {
  if (!student?.info?.id) return false

  const { catalogCourses = [] } = options
  const sportCourseIds = resolveActivitySportCourseIds(activity, catalogCourses)
  if (!sportCourseIds.length) return false

  const tier = normalizeActivityTierStatus(activity)

  for (const courseId of sportCourseIds) {
    if (!studentHasCourseAssignment(student, courseId)) continue

    if (tier === 'Basic') return true

    const enrollment = studentEnrollmentForCourseId(student, courseId)
    if (enrollment && enrollmentTierForLearningActivity(enrollment) === 'Premium') {
      return true
    }
  }

  return false
}

/** @param {unknown[]} students @param {Record<string, unknown> | null | undefined} activity @param {{ catalogCourses?: unknown[] }} [options] */
export function filterStudentsEligibleForLearningActivity(students, activity, options = {}) {
  return (students || []).filter((student) =>
    studentEligibleForLearningActivity(student, activity, options)
  )
}

export function learningActivityEligibilityMessage(activity) {
  const tier = normalizeActivityTierStatus(activity)
  if (tier === 'Premium') {
    return 'Only learners with a related course on a Premium seat license can receive this activity.'
  }
  return 'Only learners with a related course can receive this activity.'
}
